/** Backoffice API client with JWT auth + transparent refresh. */

let accessToken: string | null = localStorage.getItem("gaole_bo_access");
let refreshToken: string | null = localStorage.getItem("gaole_bo_refresh");
let refreshPromise: Promise<boolean> | null = null;

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access;
  if (access) localStorage.setItem("gaole_bo_access", access);
  else localStorage.removeItem("gaole_bo_access");
  if (refresh !== undefined) {
    refreshToken = refresh;
    if (refresh) localStorage.setItem("gaole_bo_refresh", refresh);
    else localStorage.removeItem("gaole_bo_refresh");
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Clear access token only (session lock). Refresh token stays for PIN re-login. */
export function clearAccessToken(): void {
  accessToken = null;
  localStorage.removeItem("gaole_bo_access");
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch("/api/v1/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        setTokens(data.accessToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/** Restore a valid access token from the stored refresh token (e.g. after page reload). */
export async function refreshAccessToken(): Promise<boolean> {
  return tryRefresh();
}

export async function api<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown; formData?: FormData } = {},
): Promise<T> {
  const doFetch = () =>
    fetch(`/api/v1${path}`, {
      method: options.method ?? (options.body || options.formData ? "POST" : "GET"),
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(options.body ? { "Content-Type": "application/json" } : {}),
      },
      body: options.formData ?? (options.body ? JSON.stringify(options.body) : undefined),
    });

  let res = await doFetch();
  if (res.status === 401 && refreshToken && (await tryRefresh())) {
    res = await doFetch();
  }
  if (!res.ok) {
    let message = "Something went wrong";
    let code: string | undefined;
    let details: unknown;
    try {
      const data = (await res.json()) as { error?: string; message?: string; code?: string; details?: unknown };
      message = data.error ?? data.message ?? message;
      code = data.code;
      details = data.details;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message, code, details);
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return res.json();
  return undefined as T;
}

/** Download a binary file (e.g. EasyParcel XLSX) with auth headers. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) {
    let message = "Download failed";
    let code: string | undefined;
    let details: unknown;
    try {
      const data = (await res.json()) as { error?: string; message?: string; code?: string; details?: unknown };
      message = data.error ?? data.message ?? message;
      code = data.code;
      details = data.details;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message, code, details);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
